import React, { useEffect, useState } from "react";
import { api, fmtINR, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Sparkle } from "@phosphor-icons/react";

export default function Subscription() {
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [currency, setCurrency] = useState("INR");

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/plans"), api.get("/subscription/current")]);
    setPlans(p.data);
    setCurrent(c.data);
  };
  useEffect(() => { load(); }, []);

  const upgrade = async (code) => {
    try {
      await api.post("/subscription/upgrade", { plan_code: code });
      toast.success(`Upgraded to ${code} · Razorpay checkout would open here`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const price = (p) => currency === "USD" ? `$${p.price_usd}` : `₹${p.price_inr}`;

  return (
    <div className="space-y-5 lb-fade" data-testid="subscription-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Billing</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Subscription plans</h1>
          <p className="text-neutral-600 text-sm mt-1">
            Current: <span className="font-semibold">{current?.plan_name || "—"}</span>
            <span className={"lb-badge ml-2 " + (current?.status === "active" ? "lb-badge-success" : "lb-badge-warning")}>
              {current?.status || "trial"}
            </span>
          </p>
        </div>
        <div className="flex gap-1 border border-neutral-300 p-1">
          {["INR", "USD"].map((c) => (
            <button key={c} onClick={() => setCurrency(c)}
                    className={"px-3 py-1 text-xs font-semibold " + (currency === c ? "bg-[#0A66C2] text-white" : "")}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const active = current?.plan_code === p.code;
          const highlight = p.code === "professional";
          return (
            <div key={p.code} className={"lb-card p-6 relative lb-card-hover " + (highlight ? "border-2 border-[#0A66C2]" : "")}
                 data-testid={`plan-${p.code}`}>
              {highlight && (
                <div className="absolute -top-3 left-6 bg-yellow-300 text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                  Most popular
                </div>
              )}
              <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{p.code}</div>
              <div className="font-display font-extrabold text-3xl tracking-tight mt-1">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display font-extrabold text-5xl tracking-tighter">{price(p)}</span>
                <span className="text-sm text-neutral-500">/month</span>
              </div>
              <ul className="mt-6 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-neutral-200 text-xs text-neutral-500 font-mono">
                Invoices/mo: {p.limits.invoices_per_month === -1 ? "Unlimited" : p.limits.invoices_per_month}<br />
                Users: {p.limits.users === -1 ? "Unlimited" : p.limits.users}<br />
                Companies: {p.limits.companies === -1 ? "Unlimited" : p.limits.companies}
              </div>
              <button
                disabled={active}
                onClick={() => upgrade(p.code)}
                className={"mt-6 w-full " + (active ? "lb-btn lb-btn-ghost cursor-default" : "lb-btn")}
                data-testid={`upgrade-${p.code}`}
              >
                {active ? "✓ Current plan" : <><Sparkle size={14} /> Upgrade</>}
              </button>
            </div>
          );
        })}
      </div>

      <div className="lb-card p-5 bg-neutral-50">
        <div className="text-sm text-neutral-600">
          <strong>Payment:</strong> Upgrades are processed via Razorpay. In this demo, upgrade is marked active immediately; production wires in Razorpay checkout with live keys.
        </div>
      </div>
    </div>
  );
}
